import type { AssignTaxes } from '@monorepo/shared/schemas/taxes'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/shared/lib/toast'
import { customerService } from '../services/customerService'

interface ApiError {
  response?: {
    data?: {
      error?: {
        message?: string
      }
      message?: string
    }
  }
  message?: string
}

const CUSTOMER_TAXES_QUERY_KEY = 'customer-taxes'
const CUSTOMERS_QUERY_KEY = 'customers'

// Query hooks

/**
 * Fetch customer taxes (includes customer-specific, organization, and effective taxes)
 */
export function useCustomerTaxes(customerId: string | undefined) {
  return useQuery({
    queryKey: [CUSTOMER_TAXES_QUERY_KEY, customerId],
    queryFn: () => customerService.getCustomerTaxes(customerId!),
    enabled: !!customerId,
  })
}

// Mutation hooks

/**
 * Assign taxes to customer
 * Invalidates both customer and customer-taxes queries
 */
export function useAssignCustomerTaxes() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      customerId,
      data,
    }: {
      customerId: string
      data: AssignTaxes
    }) => customerService.assignTaxes(customerId, data),
    onSuccess: (responseData, variables) => {
      // Invalidate customer taxes query
      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_TAXES_QUERY_KEY, variables.customerId],
      })
      // Invalidate customer query (to refresh tax-related fields)
      queryClient.invalidateQueries({
        queryKey: [CUSTOMERS_QUERY_KEY, variables.customerId],
      })
      queryClient.invalidateQueries({ queryKey: [CUSTOMERS_QUERY_KEY] })

      // Optionally update the cache directly for instant feedback
      queryClient.setQueryData(
        [CUSTOMER_TAXES_QUERY_KEY, variables.customerId],
        responseData
      )

      toast.success('Tax assigned successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to assign tax'
      toast.error(errorMessage)
    },
  })
}

/**
 * Remove tax from customer
 * Invalidates both customer and customer-taxes queries
 */
export function useRemoveCustomerTax() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      customerId,
      taxId,
    }: {
      customerId: string
      taxId: string
    }) => customerService.removeTax(customerId, taxId),
    onSuccess: (_data, variables) => {
      // Invalidate customer taxes query
      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_TAXES_QUERY_KEY, variables.customerId],
      })
      // Invalidate customer query (to refresh tax-related fields)
      queryClient.invalidateQueries({
        queryKey: [CUSTOMERS_QUERY_KEY, variables.customerId],
      })
      queryClient.invalidateQueries({ queryKey: [CUSTOMERS_QUERY_KEY] })

      toast.success('Tax removed successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to remove tax'
      toast.error(errorMessage)
    },
  })
}
